/*
 * Copyright (C) 2019 - Potentially Ltd
 *
 * Please see distribution for license.
 */

import { Inject, Injectable, NgZone } from '@angular/core';
import { MatLegacySnackBar as MatSnackBar } from '@angular/material/legacy-snack-bar';
import { ActivatedRoute, Router } from '@angular/router';
import { deriveAccentFamily } from '@app/app/shared/helpers/v3-brand-theme.helper';
import {
  ORGANIZATION_RETRIEVAL_DATA_SERVICE,
  OrganizationRetrievalDataService,
} from '@app/app/shared/services/organization-retrieval/organization-retrieval-data.service';
import { marker } from '@jsverse/transloco-keys-manager/marker';
import { TranslocoService } from '@ngneat/transloco';
import { Action, Selector, State, StateContext, Store } from '@ngxs/store';
import { Observable, from } from 'rxjs';
import { filter, map, tap } from 'rxjs/operators';
import { environment } from '../../../environments/environment';
import { EnvironmentCode } from '../../../environments/environment.model';
import { ACCOUNT_DATA_SERVICE, AccountDataService } from '../../page-modules/account/services/account-data.service';
import * as LanguagesActions from '../../page-modules/admin/store/languages/languages.actions';
import { ProjectHelper } from '../../page-modules/project/helpers/project.helper';
import { RedirectHelper } from '../../page-modules/resource/store/editor/content/helpers/redirect.helper';
import { REDIRECT_AFTER_LOGIN } from '../../shared/constants/constants';
import { ContentHelper } from '../../shared/helpers/content-helper';
import { CookieHelper } from '../../shared/helpers/cookie-helper';
import { LanguageCodeHelper } from '../../shared/helpers/language-code-helper';
import { PrintPageHelper } from '../../shared/helpers/print-page/print-page.helper';
import { SnackbarHelper } from '../../shared/helpers/snackbar-helper';
import { VersionHelper } from '../../shared/helpers/version.helper';
import { Organization, OrganizationAuthType, OrganizationStatus, OrganizationTheme } from '../../shared/models';
import { UserWorkspaces } from '../../shared/models/account/account.model';
import { Publisher } from '../../shared/models/content-store/content-store.model';
import { isPublicBadgeUrl } from '../../shared/paths/badge/badge-paths';
import { isPublicFolioUrl } from '../../shared/paths/folio/folio-paths';
import { isPublicSharedWorkUrl } from '../../shared/paths/sharing/shared-work-paths';
import { LanguageService } from '../../shared/services/languages/language.service';
import { V3DirectionService } from '../../shared/services/v3-language/v3-direction.service';
import { WEBSOCKET_SERVICE, WebsocketService } from '../../shared/services/websocket/websocket.service';
import { LoadableState, ObservableResult, dataLoadedState, defaultLoadableState, errorState, loadingState } from '../../shared/store';
import { UserAccountCreationRequest, UserDashboard, UserDetails, UserDetailsSummary, UserRole, dashboardId } from '../models';
import { FeatureFlag } from '../models/feature-flag.model';
import { FeatureFlagsState } from '../models/feature-flags-state.model';
import { LoginAuthError, MagicLinkLoginResponse, MagicLinkResponseType } from '../models/login-response.model';
import { AUTH_SERVICE, BasicAuthService } from '../services/basic-auth.service';
import * as UserAuthActions from './user-auth.actions';
import { GetHomePageTitleAndUri, LoadOrganizationPublisher, UnauthenticateUser, UpdateOrganizationStyles } from './user-auth.actions';

/** The account creation state details. */
export interface AccountCreationState {
  /** The details provided by the user on signup. */
  accountCreationRequest: UserAccountCreationRequest;
  /** Defines if the signup process is in progress, i.e. user attempts to signup */
  accountCreationInProgress: boolean;
  /** The error message to display when account creation failed */
  accountCreationError: string;
  /** Defines if the account creation was successful. */
  accountCreationSucceeded: boolean;
}

/** The account verification state details. */
export interface AccountVerificationState {
  /** Defines if the account verification s is in progress, i.e. user attempts has entered a verification code. */
  accountVerificationInProgress: boolean;
  /** Defines if the account verification s is in progress, i.e. user attempts has entered a verification code. */
  accountVerificationError: string;
  /** Defines if the account verification has succeeded. */
  accountVerificationSucceeded: boolean;
}

/** User state. */
export interface UserAuthStateModel {
  /** Contains the details of the user currently logged in. */
  userDetails: LoadableState<UserDetailsSummary>;
  /** Defines the account creation state. */
  accountCreationState: AccountCreationState;
  /** Defines the account verification state. */
  accountVerificationState: AccountVerificationState;
  featureFlagsState: FeatureFlagsState;
  /** Contains the details of the user organization based on the domain. */
  orgDetails: LoadableState<Organization>;
  /** Contains the details which defines if user has navigated from the sign in/up pages. */
  userNavigatedFromSignInUpPages: LoadableState<boolean>;
  /** Defines if user has previously logged in. */
  userPreviouslyLoggedIn: boolean;
  /** The number of failed login attempts. */
  loginFailureCount: number;
  /** The error type returned when consuming a magic-link sign-in token fails. */
  magicLinkLoginError: MagicLinkResponseType | null;
  /** The typed error from the most recent `TriggerUserLoginV3` attempt (v3 login form only). */
  lastLoginErrorV3: LoginAuthError | null;
  /** Defines if user submitted on board data. */
  onBoardDataSubmitted: boolean;
  /** Defines home page title. */
  homePageTitle: string;
  /** Defines home page uri for the user. */
  homePageUri: string;
  publisher: Publisher;
}

const getOrganizationDomain = () => {
  let domain: string;

  if ([EnvironmentCode.dev, EnvironmentCode.devLocal].includes(environment.env) && window.location.hostname.includes('localhost')) {
    domain = environment.devDomainOverride || 'my.yuna.dev.potential.ly';
  } else if (environment.env === EnvironmentCode.prod && window.location.hostname.includes('localhost')) {
    domain = 'my.potential.ly';
  } else {
    domain = window.location.hostname;
  }

  return domain;
};

export const LXP_ADMIN_SECTION_PRIVILEGES = [
  'can_lookup_users_of_workspace',
  'can_manage_mentors_and_mentees',
  'can_invite_members_to_organization',
  'v3_view_analytics_and_exports',
  'can_manage_group',
  'can_manage_badges',
  'can_manage_org_clients',
  'can_edit_frameworks',
  'can_manage_organization_settings',
  'can_manage_roles',
];

@State<UserAuthStateModel>({
  name: 'userAuth',
  defaults: {
    userNavigatedFromSignInUpPages: defaultLoadableState(),
    orgDetails: defaultLoadableState(),
    userDetails: defaultLoadableState(),
    homePageTitle: null,
    homePageUri: '/',
    publisher: undefined,
    accountCreationState: {
      accountCreationRequest: undefined,
      accountCreationInProgress: false,
      accountCreationError: undefined,
      accountCreationSucceeded: false,
    },
    accountVerificationState: {
      accountVerificationError: undefined,
      accountVerificationInProgress: false,
      accountVerificationSucceeded: false,
    },
    featureFlagsState: {
      aiAssistantFeatureFlagEnabled: false,
      lmsSkillsFeatureFlagEnabled: false,
      profileCertificateFeatureFlagEnabled: false,
      v3CertificatesFeatureFlagEnabled: false,
      v3CommunityFeatureFlagEnabled: false,
      tiptapEditorV3FeatureFlagEnabled: false,
      editorJsFeatureFlagEnabled: false,
      enrollmentFeatureFlagEnabled: false,
      emailNotificationsFeatureFlagEnabled: false,
      pageAnalyticsFeatureFlagEnabled: false,
      categoriesFeatureFlagEnabled: false,
      eventsTopBarHiddenFeatureFlagEnabled: false,
      tableauV2FeatureFlagEnabled: false,
      lmsCourseBadgesFeatureFlagEnabled: false,
      lmsDeepLinkingNewViewFeatureFlagEnabled: false,
      helpCenterFeatureFlagEnabled: false,
      editorJsForceFeatureFlagEnabled: false,
      scormFeatureFlagEnabled: false,
      mentorsFeatureFlagEnabled: false,
      badgesFeatureFlagEnabled: false,
      feedback360FeatureFlagEnabled: false,
      allowRecordingFeatureFlagEnabled: false,
      playlistCostFeatureFlagEnabled: false,
      newWorkbookReviewUiFeatureFlagEnabled: false,
      forceTwoFactorAuthenticationFeatureFlagEnabled: false,
      aiFeatureFlagEnabled: false,
      aiLerExpGenFeatureFlagEnabled: false,
      aiTextAnalysisFeatureFlagEnabled: false,
      documentAnnotationsFeatureFlagEnabled: false,
      googleCalendarIntegrationFeatureFlagEnabled: false,
      outlookIntegrationFeatureFlagEnabled: false,
      canvasCoursesAnalyticsFeatureFlagEnabled: false,
      newProjectDownloadFeatureFlagEnabled: false,
      analyticsDashboardsFeatureFlagEnabled: false,
      playlistAnalyticsDashboardsFeatureFlagEnabled: false,
      courseSetupDevPreviewFeatureFlagEnabled: false,
      magicLinkFeatureFlagEnabled: false,
      messageButtonOnMenteeProfileFeatureFlagEnabled: false,
      lmsAssignmentDeeplinkFeatureFlagEnabled: false,
      assignmentsFeatureFlagEnabled: false,
      ltiAssignmentsFeatureFlagEnabled: false,
      assignmentSharingFeatureFlagEnabled: false,
      formCahSubjectFeatureFlagEnabled: false,
      showPeersToLearnerFeatureFlagEnabled: false,
      showSubmissionsAndProgressToLearnerFeatureFlagEnabled: false,
    },
    userPreviouslyLoggedIn: localStorage.getItem('userPreviouslyLoggedIn') === 'true',
    loginFailureCount: 0,
    magicLinkLoginError: null,
    lastLoginErrorV3: null,
    onBoardDataSubmitted: undefined,
  },
})
@Injectable()
export class UserAuthState {
  private readonly styleElement: HTMLStyleElement;

  @Selector()
  static isAccountCreationInProgress({ accountCreationState }: UserAuthStateModel): boolean {
    return accountCreationState.accountCreationInProgress;
  }

  @Selector()
  static onBoardDataIsSubmitted({ onBoardDataSubmitted, userDetails }: UserAuthStateModel): boolean | undefined {
    return userDetails.data ? onBoardDataSubmitted : undefined;
  }

  @Selector()
  static userNavigatedFromSignInUpPages({ userNavigatedFromSignInUpPages }: UserAuthStateModel): LoadableState<boolean> {
    return userNavigatedFromSignInUpPages;
  }

  @Selector()
  static isLoginInProgress({ userDetails }: UserAuthStateModel): boolean {
    return userDetails.loading;
  }

  @Selector()
  static magicLinkLoginError({ magicLinkLoginError }: UserAuthStateModel): MagicLinkResponseType | null {
    return magicLinkLoginError;
  }

  @Selector()
  static lastLoginErrorV3({ lastLoginErrorV3 }: UserAuthStateModel): LoginAuthError | null {
    return lastLoginErrorV3;
  }

  @Selector()
  static isLoggedIn({ userDetails }: UserAuthStateModel): boolean {
    return !userDetails.error && !userDetails.loading && !!userDetails.data;
  }

  @Selector()
  static accountCreationError({ accountCreationState }: UserAuthStateModel): string {
    return accountCreationState.accountCreationError;
  }

  @Selector()
  static accountCreationSucceeded({ accountCreationState }: UserAuthStateModel): boolean {
    return accountCreationState.accountCreationSucceeded;
  }

  @Selector()
  static isAccountVerificationInProgress({ accountVerificationState }: UserAuthStateModel): boolean {
    return accountVerificationState.accountVerificationInProgress;
  }

  @Selector()
  static accountVerificationError({ accountVerificationState }: UserAuthStateModel): string {
    return accountVerificationState.accountVerificationError;
  }

  @Selector()
  static accountVerificationSucceeded({ accountVerificationState }: UserAuthStateModel): boolean {
    return accountVerificationState.accountVerificationSucceeded;
  }

  @Selector()
  static userDetailsData({ userDetails }: UserAuthStateModel): UserDetailsSummary {
    return userDetails.data;
  }

  @Selector()
  static userDetailsName({ userDetails }: UserAuthStateModel): string {
    return userDetails.data ? userDetails.data.firstName + ' ' + userDetails.data.lastName : '';
  }

  @Selector()
  static userDetailsError({ userDetails }: UserAuthStateModel): string {
    return userDetails.error;
  }

  @Selector()
  static userHasSuperAdminRole({ userDetails }: UserAuthStateModel): boolean {
    return !!userDetails.data.roles.find((role) => role.type === 'SUPER_ADMIN');
  }

  @Selector()
  static userHasAdminRole({ userDetails }: UserAuthStateModel): boolean {
    return !!userDetails.data.roles.find((role) => role.type === 'ADMIN' || role.type === 'ACCOUNT_OWNER');
  }

  @Selector()
  static userHasInstructorRole({ userDetails }: UserAuthStateModel): boolean {
    return !!userDetails.data.roles.find((role) => role.type === 'INSTRUCTOR');
  }

  // Course staff = admin / super-admin / instructor. Gates the "Marking & Assignments" surfaces.
  @Selector()
  static userHasStaffRole(state: UserAuthStateModel): boolean {
    return (
      UserAuthState.userHasAdminRole(state) || UserAuthState.userHasSuperAdminRole(state) || UserAuthState.userHasInstructorRole(state)
    );
  }

  @Selector()
  static canManageRolesAndPrivilege({ userDetails }: UserAuthStateModel): boolean {
    return this.hasPrivilege(userDetails, ['can_manage_roles_and_privileges']);
  }

  @Selector()
  static canInviteMembersToOrganization({ userDetails }: UserAuthStateModel): boolean {
    return this.hasPrivilege(userDetails, ['can_invite_members_to_organization']);
  }

  @Selector()
  static userHasCreatePlaylistPrivileges({ userDetails }: UserAuthStateModel): boolean {
    return this.hasPrivilege(userDetails, ['can_create_playlists', 'can_edit_any_playlist']);
  }

  @Selector()
  static canManageReviewsPrivileges({ userDetails }: UserAuthStateModel): boolean {
    return this.hasPrivilege(userDetails, ['can_manage_reviews']);
  }

  @Selector()
  static canViewAnalytics({ userDetails }: UserAuthStateModel): boolean {
    return this.hasPrivilege(userDetails, ['can_view_analytics_and_exports']);
  }

  @Selector()
  static canManageUserImportPrivileges({ userDetails }: UserAuthStateModel): boolean {
    return this.hasPrivilege(userDetails, ['can_manage_user_import']);
  }

  @Selector()
  static canManageMentorsAndMentees({ userDetails }: UserAuthStateModel): boolean {
    return this.hasPrivilege(userDetails, ['can_manage_mentors_and_mentees']);
  }

  @Selector()
  static canManageV3AnalyticsAndExports({ userDetails }: UserAuthStateModel): boolean {
    return this.hasPrivilege(userDetails, ['v3_view_analytics_and_exports']);
  }

  @Selector()
  static hasAnyLxpAdminSectionPrivilege({ userDetails }: UserAuthStateModel): boolean {
    return this.hasPrivilege(userDetails, LXP_ADMIN_SECTION_PRIVILEGES);
  }

  @Selector()
  static hasManageContentPrivilege(state: UserAuthStateModel): boolean {
    return (
      UserAuthState.userHasSuperAdminRole(state) ||
      UserAuthState.userHasAdminRole(state) ||
      UserAuthState.hasAnyLxpAdminSectionPrivilege(state)
    );
  }

  private static hasPrivilege(userDetails: LoadableState<UserDetailsSummary>, privilegeNames: string[]): boolean {
    const details = userDetails?.data;
    if (!details) return false;

    return details.roles.some((role) => role.privileges.some((privilege) => privilegeNames.includes(privilege.name) && privilege.assigned));
  }

  @Selector()
  static userRoles({ userDetails }: UserAuthStateModel): UserRole[] {
    return userDetails.data.roles;
  }

  @Selector()
  static userWorkspaces({ userDetails }: UserAuthStateModel): UserWorkspaces[] {
    if (userDetails.data) {
      return userDetails.data?.workspaces;
    }
    return [];
  }

  @Selector()
  static isAlumniUser({ orgDetails, userDetails }: UserAuthStateModel): boolean {
    if (userDetails.data && orgDetails.data) {
      const orgDomain = orgDetails.data?.domain;
      const workspaces = userDetails.data.workspaces;
      const currentWorkspace = workspaces.find((workspace) => (workspace.domain = orgDomain));
      if (currentWorkspace) {
        return currentWorkspace.memberDetails?.status === 'ALUMNI';
      } else {
        return false;
      }
    }
    return false;
  }

  @Selector()
  static organizationDetails({ orgDetails }: UserAuthStateModel): Organization {
    return orgDetails.data;
  }

  /** Tells "still loading" from "failed", which `organizationDetails` can't. */
  @Selector()
  static organizationDetailsState({ orgDetails }: UserAuthStateModel): LoadableState<Organization> {
    return orgDetails;
  }

  @Selector()
  static organizationName({ orgDetails }: UserAuthStateModel): string {
    return orgDetails.data.name;
  }

  @Selector()
  static organizationDomain({ orgDetails }: UserAuthStateModel): string {
    return orgDetails.data.domain;
  }

  @Selector()
  static organizationId({ orgDetails }: UserAuthStateModel): string {
    return orgDetails.data._id;
  }

  @Selector()
  static userPreviouslyLoggedIn({ userPreviouslyLoggedIn }: UserAuthStateModel): boolean {
    return userPreviouslyLoggedIn;
  }

  @Selector()
  static loginFailureCount({ loginFailureCount }: UserAuthStateModel): number {
    return loginFailureCount;
  }

  @Selector()
  static isDiscoveryPageAvailable({ orgDetails, userDetails }: UserAuthStateModel): boolean {
    return (
      orgDetails.data.discoveryPage.status === OrganizationStatus.ACTIVE ||
      !!userDetails.data.roles.find((role) => role.type === 'ADMIN' || role.type === 'ACCOUNT_OWNER')
    );
  }

  @Selector()
  static canAddNewWorkspace({ orgDetails, userDetails }: UserAuthStateModel): boolean {
    if (orgDetails.data && userDetails.data) {
      const hasNoFolioTypeWorkspace = !userDetails.data.workspaces.find((item) => item.type === 'FOLIO');
      return hasNoFolioTypeWorkspace && orgDetails.data.type !== 'FOLIO';
    }

    return false;
  }

  @Selector()
  static getHomePageTitle({ homePageTitle }: UserAuthStateModel): string {
    return homePageTitle;
  }

  @Selector()
  static homePageUri({ homePageUri }: UserAuthStateModel): string {
    return homePageUri;
  }

  @Selector()
  static getOrganizationPublisher({ publisher }: UserAuthStateModel): Publisher {
    return publisher;
  }

  @Selector()
  static featureFlags({ featureFlagsState }: UserAuthStateModel): FeatureFlagsState {
    const merged = { ...featureFlagsState, ...(environment.featureFlagOverrides || {}) };
    // ltiAssignmentsEnabled implies assignmentsEnabled — resolved once here so that
    // guards (which read this aggregate selector by key) only ever check the base flag.
    merged.assignmentsFeatureFlagEnabled = merged.assignmentsFeatureFlagEnabled || merged.ltiAssignmentsFeatureFlagEnabled;
    return merged;
  }

  @Selector()
  static courseSetupDevPreviewFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return (
      environment.featureFlagOverrides?.courseSetupDevPreviewFeatureFlagEnabled ?? featureFlagsState.courseSetupDevPreviewFeatureFlagEnabled
    );
  }

  @Selector()
  static lmsAssignmentDeeplinkFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return (
      environment.featureFlagOverrides?.lmsAssignmentDeeplinkFeatureFlagEnabled ?? featureFlagsState.lmsAssignmentDeeplinkFeatureFlagEnabled
    );
  }

  @Selector()
  static ltiAssignmentsFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return environment.featureFlagOverrides?.ltiAssignmentsFeatureFlagEnabled ?? featureFlagsState.ltiAssignmentsFeatureFlagEnabled;
  }

  @Selector()
  static formCahSubjectFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return environment.featureFlagOverrides?.formCahSubjectFeatureFlagEnabled ?? featureFlagsState.formCahSubjectFeatureFlagEnabled;
  }

  /**
   * Whether an LTI launch should land on the frameless course-setup view *instead of* a legacy picker.
   * `lms_assignment_deeplink` is what turns it on, but its route tree (`/lti/course/setup/:courseUri`)
   * is guarded on `lti_assignments` — so an entry point may only redirect when both are on, or
   * `FeatureFlagsGuard` bounces the instructor to the app home page, inside the LMS iframe.
   *
   * **v3 only.** `lms_assignment_deeplink` has no meaning on v2: nothing there can consume the new view
   * as a landing, and the legacy pickers own affordances it has no equivalent of. The v2 route into the
   * new view is `ptl-lti-course-setup-promo`, which gates on `lti_assignments` alone. The landing
   * components pair this selector with the organisation's UI version; do not read it without that.
   */
  @Selector()
  static ltiCourseSetupViewEnabled({ featureFlagsState }: UserAuthStateModel): boolean {
    const overrides = environment.featureFlagOverrides;
    return (
      (overrides?.lmsAssignmentDeeplinkFeatureFlagEnabled ?? featureFlagsState.lmsAssignmentDeeplinkFeatureFlagEnabled) &&
      (overrides?.ltiAssignmentsFeatureFlagEnabled ?? featureFlagsState.ltiAssignmentsFeatureFlagEnabled)
    );
  }

  // ltiAssignmentsEnabled implies assignmentsEnabled — resolved once here so that
  // consumers only ever check the base flag.
  @Selector()
  static assignmentsFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    const base = environment.featureFlagOverrides?.assignmentsFeatureFlagEnabled ?? featureFlagsState.assignmentsFeatureFlagEnabled;
    const lti = environment.featureFlagOverrides?.ltiAssignmentsFeatureFlagEnabled ?? featureFlagsState.ltiAssignmentsFeatureFlagEnabled;
    return base || lti;
  }

  /** Sharing lives inside assignments, so it takes both flags. */
  @Selector()
  static assignmentSharingFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    const overrides = environment.featureFlagOverrides;
    const assignments =
      (overrides?.assignmentsFeatureFlagEnabled ?? featureFlagsState.assignmentsFeatureFlagEnabled) ||
      (overrides?.ltiAssignmentsFeatureFlagEnabled ?? featureFlagsState.ltiAssignmentsFeatureFlagEnabled);
    const sharing = overrides?.assignmentSharingFeatureFlagEnabled ?? featureFlagsState.assignmentSharingFeatureFlagEnabled;
    return assignments && sharing;
  }

  @Selector()
  static aiFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.aiFeatureFlagEnabled;
  }

  @Selector()
  static aiAssistantFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.aiAssistantFeatureFlagEnabled;
  }

  @Selector()
  static lmsSkillsFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.lmsSkillsFeatureFlagEnabled;
  }

  @Selector()
  static lmsCourseBadgesFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.lmsCourseBadgesFeatureFlagEnabled;
  }

  @Selector()
  static lmsDeepLinkingNewViewFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.lmsDeepLinkingNewViewFeatureFlagEnabled;
  }

  @Selector()
  static enrollmentFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.enrollmentFeatureFlagEnabled;
  }

  @Selector()
  static emailNotificationsFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.emailNotificationsFeatureFlagEnabled;
  }

  @Selector()
  static pageAnalyticsFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.pageAnalyticsFeatureFlagEnabled;
  }

  @Selector()
  static categoriesFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.categoriesFeatureFlagEnabled;
  }

  @Selector()
  static tableauV2FeatureFlagEnabled({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.tableauV2FeatureFlagEnabled;
  }

  @Selector()
  static profileCertFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.profileCertificateFeatureFlagEnabled;
  }

  @Selector()
  static v3CertificatesFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.v3CertificatesFeatureFlagEnabled;
  }

  @Selector()
  static v3CommunityFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.v3CommunityFeatureFlagEnabled;
  }

  @Selector()
  static editorJsForceFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.editorJsForceFeatureFlagEnabled;
  }

  @Selector()
  static editorJsFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.editorJsFeatureFlagEnabled;
  }

  @Selector()
  static tiptapEditorV3FeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.tiptapEditorV3FeatureFlagEnabled;
  }

  @Selector()
  static eventsTopBarHiddenFeatureFlagEnabled({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.eventsTopBarHiddenFeatureFlagEnabled;
  }

  @Selector()
  static helpCenterFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.helpCenterFeatureFlagEnabled;
  }

  @Selector()
  static playlistCostFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.playlistCostFeatureFlagEnabled;
  }

  @Selector()
  static forceTwoFactorAuthenticationFeatureFlagEnabled({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.forceTwoFactorAuthenticationFeatureFlagEnabled;
  }

  // v3-only org setting, self-serve from Organization Settings — distinct from the super-admin-managed
  // `force_two_factor_authentication` feature flag above.
  @Selector()
  static organizationTwoFactorAuthRequired({ orgDetails }: UserAuthStateModel): boolean {
    return !!orgDetails.data?.twoFactorAuthRequired;
  }

  // What every v3 call site (sign-in form, account settings, ForceTwoFactorAuthGuard) should actually
  // check: the org's own setting on v3, the legacy feature flag everywhere else (v2).
  @Selector()
  static shouldEnforceTwoFactorAuth(state: UserAuthStateModel): boolean {
    return VersionHelper.isV3Enabled()
      ? UserAuthState.organizationTwoFactorAuthRequired(state)
      : UserAuthState.forceTwoFactorAuthenticationFeatureFlagEnabled(state);
  }

  @Selector()
  static magicLinkFeatureFlagEnabled({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.magicLinkFeatureFlagEnabled;
  }

  @Selector()
  static messageButtonOnMenteeProfileFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.messageButtonOnMenteeProfileFeatureFlagEnabled;
  }

  @Selector()
  static scormFeatureFlagEnabled({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.scormFeatureFlagEnabled;
  }

  @Selector()
  static mentorsFeatureFlagEnabled({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.mentorsFeatureFlagEnabled;
  }

  @Selector()
  static badgesFeatureFlagEnabled({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.badgesFeatureFlagEnabled;
  }

  @Selector()
  static feedback360FeatureFlagEnabled({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.feedback360FeatureFlagEnabled;
  }

  @Selector()
  static allowRecordingFeatureFlagEnabled({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.allowRecordingFeatureFlagEnabled;
  }

  @Selector()
  static newWorkbookReviewUiFeatureFlagEnabled({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.newWorkbookReviewUiFeatureFlagEnabled;
  }

  @Selector()
  static aiLerExpGenEnabled({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.aiFeatureFlagEnabled && featureFlagsState.aiLerExpGenFeatureFlagEnabled;
  }

  @Selector()
  static aiTextAnalysisFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.aiTextAnalysisFeatureFlagEnabled;
  }

  @Selector()
  static documentAnnotationsEnabled({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.documentAnnotationsFeatureFlagEnabled;
  }

  @Selector()
  static googleCalendarIntegrationFeatureFlagEnabled({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.googleCalendarIntegrationFeatureFlagEnabled;
  }

  @Selector()
  static outlookIntegrationFeatureFlagEnabled({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.outlookIntegrationFeatureFlagEnabled;
  }

  @Selector()
  static showPeersToLearnerFeatureFlagEnabled({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.showPeersToLearnerFeatureFlagEnabled;
  }

  @Selector()
  static showSubmissionsAndProgressToLearnerFeatureFlagEnabled({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.showSubmissionsAndProgressToLearnerFeatureFlagEnabled;
  }

  @Selector()
  static canvasCoursesAnalyticsFeatureFlagEnabled({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.canvasCoursesAnalyticsFeatureFlagEnabled;
  }

  @Selector()
  static newProjectDownloadFeatureFlag({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.newProjectDownloadFeatureFlagEnabled;
  }

  @Selector()
  static analyticsDashboardsFeatureFlagEnabled({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.analyticsDashboardsFeatureFlagEnabled;
  }

  @Selector()
  static playlistAnalyticsDashboardsFeatureFlagEnabled({ featureFlagsState }: UserAuthStateModel): boolean {
    return featureFlagsState.playlistAnalyticsDashboardsFeatureFlagEnabled;
  }

  @Selector()
  static orgClientAdminOf({ userDetails }: UserAuthStateModel): string | undefined {
    return userDetails.data?.orgClientAdminOf?.[0];
  }

  @Selector()
  static userDashboard({ userDetails }: UserAuthStateModel): UserDashboard | undefined {
    return userDetails.data?.dashboard;
  }

  @Selector()
  static clientOrgId({ userDetails }: UserAuthStateModel): string | null {
    return dashboardId(userDetails.data?.dashboard);
  }

  static setPreviousUrlAfterLogin(url?: string) {
    const previousUrlPath = !url ? this.checkIfShouldSetUrl() : url;
    if (previousUrlPath) {
      localStorage.setItem(REDIRECT_AFTER_LOGIN, previousUrlPath);
    }
  }

  static checkIfShouldSetUrl() {
    if (
      location.pathname.includes('/account-verification') ||
      location.pathname.includes('/signin') ||
      location.pathname.includes('/signup') ||
      location.pathname.includes('/invitation') ||
      location.pathname.includes('/support/signin') ||
      location.pathname.includes('/external/signin') ||
      location.pathname.includes('/signin/classof2020') ||
      location.pathname.includes('/graduate/signin') ||
      location.pathname.includes('/signup/classof2020') ||
      location.pathname.includes('/lti/launch') ||
      location.pathname.includes('/lti/deeplink') ||
      location.pathname.includes('/lti/course') ||
      location.pathname.includes('/saml/signin') ||
      location.pathname.includes('/saml/signin-error') ||
      location.pathname.includes('/lti/playlists/edit') ||
      location.pathname.includes('/lti/registration/error') ||
      location.pathname.includes('/reset-password') ||
      location.pathname.includes('/forgot-password') ||
      location.pathname.includes('/reset-email') ||
      location.pathname.includes('/verify-email') ||
      location.pathname.includes('/confirm-email') ||
      location.pathname.includes('/unverified-email') ||
      location.pathname.includes('/verify-personal-email') ||
      location.pathname.includes('/verify-alumni-email') ||
      location.pathname.includes('/verify-pre-arrival') ||
      location.pathname.includes('/verify-pre-arrival-with-folio') ||
      location.pathname.includes('/verify-organisation-email') ||
      location.pathname.includes('/verify-organisation-email-with-folio') ||
      location.pathname.includes('/prearrivals/signin') ||
      location.pathname.includes('/prearrivals/signup') ||
      location.pathname.includes('/prearrivals/forgot-password') ||
      location.pathname.includes('/verify-folio') ||
      location.pathname.includes('/verify-alumni') ||
      location.pathname.includes('/iframe-logout') ||
      location.pathname.includes('/folio/badge') ||
      location.pathname.includes('/public/portfolio') ||
      isPublicBadgeUrl(location.pathname) ||
      isPublicFolioUrl(location.pathname) ||
      isPublicSharedWorkUrl(location.pathname)
    ) {
      return '';
    }
    return location.pathname + location.search;
  }

  constructor(
    @Inject(AUTH_SERVICE) private authService: BasicAuthService,
    @Inject(ORGANIZATION_RETRIEVAL_DATA_SERVICE) private organizationRetrievalService: OrganizationRetrievalDataService,
    @Inject(ACCOUNT_DATA_SERVICE) private accountService: AccountDataService,
    @Inject(WEBSOCKET_SERVICE) private websocketService: WebsocketService,
    private languageService: LanguageService,
    private translocoService: TranslocoService,
    private store: Store,
    private router: Router,
    private snackBar: MatSnackBar,
    private activatedRoute: ActivatedRoute,
    private ngZone: NgZone,
    private directionService: V3DirectionService,
  ) {
    this.styleElement = document.createElement('style');
    document.head.appendChild(this.styleElement);
  }

  @Action(UserAuthActions.UserLoginInit)
  userLoginInit(context: StateContext<UserAuthStateModel>) {
    const { patchState } = context;
    patchState({
      loginFailureCount: 0,
    });
  }

  @Action(UserAuthActions.TriggerUserLogin)
  triggerUserLogin(
    context: StateContext<UserAuthStateModel>,
    { email, password, totp }: UserAuthActions.TriggerUserLogin,
  ): ObservableResult<UserDetails> {
    const { patchState, dispatch } = context;
    patchState({
      userDetails: loadingState(),
    });
    const organizationDomain = this.store.selectSnapshot(UserAuthState.organizationDetails)?.domain;
    return this.authService
      .authenticate(email, password, organizationDomain, totp)
      .pipe(
        tap(({ isSuccess, value, error }) =>
          isSuccess ? this.handleSuccessfulAuthentication(value, context, email) : dispatch(new UserAuthActions.UserLoginFailed(error)),
        ),
      );
  }

  @Action(UserAuthActions.TriggerUserLoginV3)
  triggerUserLoginV3(
    context: StateContext<UserAuthStateModel>,
    { email, password, totp }: UserAuthActions.TriggerUserLoginV3,
  ): Observable<{ isSuccess: boolean; value?: UserDetails; error?: LoginAuthError }> {
    const { patchState } = context;
    patchState({
      userDetails: loadingState(),
      lastLoginErrorV3: null,
    });
    const organizationDomain = this.store.selectSnapshot(UserAuthState.organizationDetails)?.domain;
    return this.authService.authenticateWithTypedError(email, password, organizationDomain, totp).pipe(
      tap(({ isSuccess, value, error }) => {
        if (isSuccess) {
          this.handleSuccessfulAuthentication(value, context, email);
        } else {
          // Deliberately does NOT dispatch `UserLoginFailed` (failure-count/lockout-warning) here —
          // Keycloak can't reliably distinguish "wrong password" from "correct password, TOTP code
          // needed" for every realm (confirmed: both can return identical `invalid_grant` /
          // "Invalid user credentials"), so the caller decides whether a given attempt should count
          // as a real failure once it knows whether the user still had a chance to supply a code.
          patchState({ userDetails: defaultLoadableState(), lastLoginErrorV3: error });
        }
      }),
    );
  }

  @Action(UserAuthActions.TriggerFolioUserLogin)
  triggerFolioUserLogin(
    context: StateContext<UserAuthStateModel>,
    { email, password, targetOrganization }: UserAuthActions.TriggerFolioUserLogin,
  ): ObservableResult<UserDetails> {
    const { patchState, dispatch } = context;
    patchState({
      userDetails: loadingState(),
    });
    const organizationDomain = this.store.selectSnapshot(UserAuthState.organizationDetails)?.domain;
    return this.authService
      .authenticate(email, password, organizationDomain)
      .pipe(
        tap(({ isSuccess, error }) =>
          isSuccess
            ? this.handleSuccessfulFolioUserAuthentication(context, targetOrganization, email)
            : dispatch(new UserAuthActions.UserLoginFailed(error)),
        ),
      );
  }

  @Action(UserAuthActions.TriggerMagicLinkLogin)
  triggerMagicLinkLogin(
    context: StateContext<UserAuthStateModel>,
    { token }: UserAuthActions.TriggerMagicLinkLogin,
  ): ObservableResult<MagicLinkLoginResponse> {
    const { patchState, dispatch } = context;
    patchState({
      userDetails: loadingState(),
      magicLinkLoginError: null,
    });
    return this.authService
      .consumeMagicLinkToken(token)
      .pipe(
        tap(({ value }) =>
          value.type === 'AUTH_SUCCESS'
            ? this.handleSuccessfulMagicLinkAuthentication(value.user, context)
            : dispatch(new UserAuthActions.MagicLinkLoginFailed(value.type, value.message)),
        ),
      );
  }

  @Action(UserAuthActions.MagicLinkLoginFailed)
  magicLinkLoginFailed({ patchState }: StateContext<UserAuthStateModel>, { errorType }: UserAuthActions.MagicLinkLoginFailed) {
    patchState({
      userDetails: defaultLoadableState(),
      magicLinkLoginError: errorType,
    });
  }

  private handleSuccessfulMagicLinkAuthentication(
    userDetails: UserDetails,
    { getState, patchState, dispatch }: StateContext<UserAuthStateModel>,
  ): void {
    const state = getState();
    const currentOrganization = state.orgDetails.data;
    if (!currentOrganization) {
      dispatch(new UserAuthActions.MagicLinkLoginFailed('AUTH_ERROR'));
      return;
    }
    userDetails.organization = {
      _id: currentOrganization._id,
      name: currentOrganization.name,
    };
    patchState({
      userDetails: dataLoadedState(userDetails),
      loginFailureCount: 0,
    });
    this.proceed(patchState, currentOrganization);
    this.websocketService.openConnection();
  }

  private handleSuccessfulAuthentication(
    userDetails: UserDetails,
    { getState, patchState }: StateContext<UserAuthStateModel>,
    email: string,
  ): void {
    const state = getState();
    const currentOrganization = state.orgDetails.data;
    userDetails.organization = {
      _id: currentOrganization._id,
      name: currentOrganization.name,
    };
    patchState({
      userDetails: dataLoadedState(userDetails),
      loginFailureCount: 0,
    });
    this.authService.finalize(email).subscribe(({ isSuccess, error }) => {
      if (isSuccess) {
        this.proceed(patchState, currentOrganization);
        this.websocketService.openConnection();
      } else if (error) {
        SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, error);
      }
    });
  }

  private handleSuccessfulFolioUserAuthentication(
    { getState, patchState }: StateContext<UserAuthStateModel>,
    destinationOrganization: string,
    email: string,
  ): void {
    const state = getState();
    const currentOrganization = state.orgDetails.data;
    this.authService.finalizeFolioLogin(destinationOrganization, true, email).subscribe(({ value, isSuccess, error }) => {
      if (isSuccess) {
        this.proceed(patchState, currentOrganization);
        this.websocketService.openConnection();
      } else if (error) {
        localStorage.setItem('user_is_logged_in', 'false');
        localStorage.removeItem('auth_access_token');
        this.setPreviouslyLoggedInState(false, patchState);
        SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, error);
      }
    });
  }

  private proceed(patchState: (val: Partial<UserAuthStateModel>) => UserAuthStateModel, organization: Organization): void {
    this.setPreviouslyLoggedInState(true, patchState);
    this.accountService.getPersonalData().subscribe((data) => {
      const userLanguage = data.value?.userSettings.language;
      const storedLanguage = this.languageService.getLanguage();
      this.languageService.setLanguage(userLanguage !== storedLanguage ? userLanguage : storedLanguage);
      this.checkUserSelectedLanguageOrientation(organization, userLanguage !== storedLanguage ? userLanguage : storedLanguage);
      this.store.dispatch(new UserAuthActions.UpdateUserPersonalDetails(data.value));
      this.store.dispatch(new LoadOrganizationPublisher());
      this.store
        .dispatch(new GetHomePageTitleAndUri())
        .toPromise()
        .then(() => this.redirectFurther());
      this.setOnBoardingDataSubmitStatus(data.value.onBoardingSubmitted, patchState);
    });
  }

  private redirectFurther() {
    let redirectAfterLoginUrl = CookieHelper.GetCookie(REDIRECT_AFTER_LOGIN);
    if (!redirectAfterLoginUrl) {
      redirectAfterLoginUrl = localStorage.getItem(REDIRECT_AFTER_LOGIN);
    }
    if (redirectAfterLoginUrl) {
      RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, redirectAfterLoginUrl);
      localStorage.removeItem(REDIRECT_AFTER_LOGIN);
      CookieHelper.DeleteCookie(REDIRECT_AFTER_LOGIN);
    } else {
      const homePageUri = this.store.selectSnapshot(UserAuthState.homePageUri);
      RedirectHelper.redirectToHomePage(this.ngZone, this.router, this.activatedRoute, homePageUri);
    }
  }

  @Action(UserAuthActions.TriggerUserAccountCreation)
  triggerAccountCreation(
    { patchState, dispatch, getState }: StateContext<UserAuthStateModel>,
    { accountCreationRequest, invitationId, token }: UserAuthActions.TriggerUserAccountCreation,
  ) {
    const orgDetails = getState().orgDetails.data;

    patchState({
      accountCreationState: {
        ...getState().accountCreationState,
        accountCreationRequest,
        accountCreationInProgress: true,
      },
    });

    if (invitationId) {
      return this.authService
        .createAccountWithInvitationToken(accountCreationRequest, invitationId, token, orgDetails.domain)
        .pipe(
          map(({ isSuccess, error }) =>
            isSuccess
              ? dispatch(new UserAuthActions.UserAccountCreationSuccess(false, true))
              : dispatch(new UserAuthActions.UserAccountCreationError(error)),
          ),
        );
    } else {
      return this.authService
        .createAccount(accountCreationRequest, orgDetails._id, orgDetails.domain, orgDetails.name)
        .pipe(
          map(({ isSuccess, error }) =>
            isSuccess
              ? dispatch(new UserAuthActions.UserAccountCreationSuccess(true, false))
              : dispatch(new UserAuthActions.UserAccountCreationError(error)),
          ),
        );
    }
  }

  @Action(UserAuthActions.UserAccountCreationSuccess)
  accountCreationSuccess(
    { patchState, getState }: StateContext<UserAuthStateModel>,
    { emailVerificationIsNeeded, isInvitationRegistration }: UserAuthActions.UserAccountCreationSuccess,
  ) {
    patchState({
      accountCreationState: {
        ...getState().accountCreationState,
        accountCreationInProgress: false,
        accountCreationSucceeded: true,
      },
    });

    const orgDetails = getState().orgDetails.data;

    // For invitation-driven registration with non-FORM authType, redirect to /support/signin
    const shouldRedirectToSupport = isInvitationRegistration && orgDetails?.authType !== OrganizationAuthType.FORM;

    let url = emailVerificationIsNeeded ? '/unverified-email' : '/signin';
    if (shouldRedirectToSupport && !emailVerificationIsNeeded) {
      url = '/support/signin';
    }

    const extraParams = emailVerificationIsNeeded
      ? {
          queryParams: {
            email: getState().accountCreationState.accountCreationRequest.email,
            register: true,
          },
        }
      : undefined;

    RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, url, extraParams);
  }

  @Action(UserAuthActions.UserAccountCreationError)
  accountCreationError({ patchState, getState }: StateContext<UserAuthStateModel>, { error }: UserAuthActions.UserAccountCreationError) {
    patchState({
      accountCreationState: {
        ...getState().accountCreationState,
        accountCreationInProgress: false,
        accountCreationSucceeded: false,
        accountCreationError: error,
      },
    });
  }

  @Action(UserAuthActions.TriggerUserAccountVerification)
  triggerAccountVerification(
    { patchState, dispatch, getState }: StateContext<UserAuthStateModel>,
    { token }: UserAuthActions.TriggerUserAccountVerification,
  ) {
    patchState({
      accountVerificationState: {
        ...getState().accountVerificationState,
        accountVerificationInProgress: true,
      },
    });

    return this.authService
      .verifyAccount(token)
      .pipe(
        map(({ isSuccess, error }) =>
          isSuccess
            ? dispatch(new UserAuthActions.UserAccountVerificationSuccess())
            : dispatch(new UserAuthActions.TriggerUserAccountVerificationError(error)),
        ),
      );
  }

  @Action(UserAuthActions.UserAccountVerificationSuccess)
  accountVerificationSuccess({ patchState, getState }: StateContext<UserAuthStateModel>) {
    patchState({
      accountVerificationState: {
        ...getState().accountVerificationState,
        accountVerificationInProgress: false,
        accountVerificationSucceeded: true,
      },
    });

    const message = this.translocoService.translate('translations.public.message.success.accountVerified');
    RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, '/signin');
    this.ngZone.run(() => {
      this.snackBar.open(message, '', {
        panelClass: 'success',
        politeness: 'polite',
        duration: 4000,
        verticalPosition: 'top',
        horizontalPosition: 'center',
      });
    });
  }

  @Action(UserAuthActions.TriggerUserAccountVerificationError)
  accountVerificationError(
    { patchState, getState }: StateContext<UserAuthStateModel>,
    { error }: UserAuthActions.TriggerUserAccountVerificationError,
  ) {
    patchState({
      accountVerificationState: {
        ...getState().accountVerificationState,
        accountVerificationError: error,
      },
    });
  }

  @Action(UserAuthActions.RedirectToAuthenticatedApp)
  redirectToAuthenticatedApp({ dispatch, patchState }: StateContext<UserAuthStateModel>) {
    return this.accountService.getPersonalData().pipe(
      filter((data) => !!data),
      map(({ isSuccess, value }) => {
        if (isSuccess && value) {
          this.setOnBoardingDataSubmitStatus(value.onBoardingSubmitted, patchState);
          const userLanguage = value.userSettings.language;
          const storedLanguage = this.languageService.getLanguage();
          const organization = this.store.selectSnapshot(UserAuthState.organizationDetails);
          this.checkUserSelectedLanguageOrientation(organization, userLanguage !== storedLanguage ? userLanguage : storedLanguage);
          this.languageService.setLanguage(userLanguage !== storedLanguage ? userLanguage : storedLanguage);
          this.store.dispatch(new UserAuthActions.UpdateUserPersonalDetails(value));
        }
        return isSuccess
          ? dispatch([new UserAuthActions.RedirectToAuthenticatedAppSuccess()])
          : dispatch(new UserAuthActions.RedirectToAuthenticatedAppFailure());
      }),
    );
  }

  @Action(UserAuthActions.UserLoginFailed)
  userLoginFailed({ patchState, getState }: StateContext<UserAuthStateModel>) {
    const { loginFailureCount } = getState();
    this.setPreviouslyLoggedInState(false, patchState);

    patchState({
      userDetails: defaultLoadableState(),
      loginFailureCount: loginFailureCount + 1,
    });
  }

  @Action(UserAuthActions.UpdateUserPersonalDetails)
  updateUserPersonalDetails(
    { patchState, getState }: StateContext<UserAuthStateModel>,
    { userPersonalData }: UserAuthActions.UpdateUserPersonalDetails,
  ) {
    patchState({
      ...getState(),
      userDetails: {
        ...getState().userDetails,
        data: {
          ...getState().userDetails.data,
          uid: userPersonalData.uid,
          firstName: userPersonalData.firstName,
          lastName: userPersonalData.lastName,
          email: userPersonalData.email,
          imageUrl: userPersonalData.imageUrl ? userPersonalData.imageUrl : 'assets/profile_image.png',
          description: userPersonalData.description,
          roles: userPersonalData.roles,
          groups: userPersonalData.groups,
          organization: getState().orgDetails?.data
            ? { _id: getState().orgDetails.data._id, name: getState().orgDetails.data.name }
            : userPersonalData.organization,
          workspaces: userPersonalData.workspaces,
          userSettings: userPersonalData.userSettings,
          eptid: userPersonalData.eptid,
          orgClientAdminOf: userPersonalData.orgClientAdminOf,
          dashboard: userPersonalData.dashboard,
        },
      },
    });

    this.setPreviouslyLoggedInState(true, patchState);
  }

  @Action(UserAuthActions.UpdateUserJiscUsage)
  updateUserJiscUsage({ patchState, getState }: StateContext<UserAuthStateModel>, { hasUsedCpd }: UserAuthActions.UpdateUserJiscUsage) {
    const organizationId = getState().orgDetails.data._id;
    this.accountService.updateJiscUsage(hasUsedCpd).subscribe(({ isSuccess, error }) => {
      if (isSuccess) {
        patchState({
          ...getState(),
          userDetails: {
            ...getState().userDetails,
            data: {
              ...getState().userDetails.data,
              workspaces: getState().userDetails.data.workspaces.map((item) => {
                if (item.organizationUid === organizationId) {
                  item.memberDetails.hasUsedCpd = hasUsedCpd;
                }
                return item;
              }),
            },
          },
        });
      } else if (error) {
        SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, error);
      }
    });
  }

  @Action(UserAuthActions.RedirectToAuthenticatedAppSuccess)
  redirectToAuthenticatedAppSuccess() {
    if (location.pathname.includes('/signin') || location.pathname.includes('/signup') || !!location.hash) {
      // Wrapped with ngZone to prevent Angular timing rendering issues
      const redirectAfterLoginUrl = localStorage.getItem(REDIRECT_AFTER_LOGIN);
      if (redirectAfterLoginUrl) {
        RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, redirectAfterLoginUrl);
        localStorage.removeItem(REDIRECT_AFTER_LOGIN);
      } else {
        const homePageUri = this.store.selectSnapshot(UserAuthState.homePageUri);
        RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, homePageUri);
      }
    }
  }

  @Action(UserAuthActions.RedirectToAuthenticatedAppFailure)
  redirectToAuthenticatedFailure() {
    if (!location.pathname.includes('/account-verification')) {
      if (
        location.pathname.includes('/signup') &&
        !location.pathname.includes('/signup/classof2020') &&
        !location.pathname.includes('/prearrivals/signup')
      ) {
        RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, '/signup');
        return;
      }
      if (
        location.pathname.includes('/support/signin') ||
        location.pathname.includes('/external/signin') ||
        location.pathname.includes('/graduate/signin') ||
        location.pathname.includes('/signin/classof2020') ||
        location.pathname.includes('/signup/classof2020') ||
        location.pathname.includes('/invitation/') ||
        location.pathname.includes('/reset-password') ||
        location.pathname.includes('/forgot-password') ||
        location.pathname.includes('/reset-email') ||
        location.pathname.includes('/verify-email') ||
        location.pathname.includes('/confirm-email') ||
        location.pathname.includes('/unverified-email') ||
        location.pathname.includes('/verify-personal-email') ||
        location.pathname.includes('/verify-alumni-email') ||
        location.pathname.includes('/verify-pre-arrival') ||
        location.pathname.includes('/verify-pre-arrival-with-folio') ||
        location.pathname.includes('/verify-organisation-email') ||
        location.pathname.includes('/verify-organisation-email-with-folio') ||
        location.pathname.includes('/prearrivals/signin') ||
        location.pathname.includes('/prearrivals/signup') ||
        location.pathname.includes('/prearrivals/forgot-password') ||
        location.pathname.includes('/verify-folio') ||
        location.pathname.includes('/verify-alumni') ||
        location.pathname.includes('/verify-pre-arrival') ||
        location.pathname.includes('/verify-pre-arrival-with-folio') ||
        location.pathname.includes('/lti/launch') ||
        location.pathname.includes('/lti/deeplink') ||
        location.pathname.includes('/lti/course') ||
        location.pathname.includes('/saml/signin') ||
        location.pathname.includes('/lti/playlists/edit') ||
        location.pathname.includes('/iframe-logout') ||
        location.pathname.includes('/saml/signin-error') ||
        location.pathname.includes('/lti/registration/error') ||
        location.pathname.includes('/folio/badge') ||
        location.pathname.includes('/public/portfolio') ||
        location.pathname.includes('/certificates/verify') ||
        isPublicBadgeUrl(location.pathname) ||
        isPublicFolioUrl(location.pathname) ||
        isPublicSharedWorkUrl(location.pathname) ||
        ProjectHelper.isProjectPublicView()
      ) {
        return;
      }
      if (!localStorage.getItem(REDIRECT_AFTER_LOGIN)) {
        UserAuthState.setPreviousUrlAfterLogin();
      }
      if (ContentHelper.isFrameMode()) {
        // for iframe redirect to iframe error page
        RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, '/iframe-logout');
        return;
      }
      RedirectHelper.redirectByUrl(this.ngZone, this.router, this.activatedRoute, '/signin');
    }
  }

  /**
   * Returned rather than subscribed so that `dispatch(...)` resolves only once the roles are in the
   * store. Callers are auth landings that route on what they find there — an LTI launch picks the
   * marking or the learner view straight off `userHasInstructorRole` — and a fire-and-forget fetch
   * left that decision racing whichever other action in the same dispatch happened to finish last.
   */
  @Action(UserAuthActions.FetchUserDetails)
  fetchUserDetails({ getState, patchState, dispatch }: StateContext<UserAuthStateModel>): Observable<unknown> {
    patchState({
      userDetails: loadingState(),
    });
    return this.usePersonalData(patchState, getState, dispatch);
  }

  @Action(UserAuthActions.SendOnBoardingData)
  sendOnBoardingData({ patchState }: StateContext<UserAuthStateModel>, { data }: UserAuthActions.SendOnBoardingData) {
    this.setOnBoardingDataSubmitStatus(true, patchState);
    this.authService.sendOnBoardData(data).subscribe();
  }

  @Action(UserAuthActions.GetHomePageTitleAndUri)
  getHomePageTitle({ patchState, getState }: StateContext<UserAuthStateModel>) {
    const homePageTitle = getState().homePageTitle;
    const homePageUri = getState().homePageUri;
    if (homePageTitle && homePageUri) {
      return false;
    }
    const isAlumni = this.store.selectSnapshot(UserAuthState.isAlumniUser);
    return this.organizationRetrievalService.getHomePage(isAlumni).pipe(
      tap(({ isSuccess, value, error }) => {
        if (isSuccess) {
          patchState({
            homePageTitle: value.headline,
            homePageUri: value.type === 'HOME_PAGE' ? '/' : value.uri,
          });
        } else {
          patchState({
            homePageTitle: '',
            homePageUri: '/',
          });
        }
      }),
    );
  }

  @Action(UserAuthActions.UpdateHomePageTitle)
  updateHomePageTitle({ patchState }: StateContext<UserAuthStateModel>, { title }: UserAuthActions.UpdateHomePageTitle) {
    patchState({
      homePageTitle: title,
    });
  }

  private setOnBoardingDataSubmitStatus(status: boolean, patchState: (val: Partial<UserAuthStateModel>) => UserAuthStateModel) {
    patchState({
      onBoardDataSubmitted: status,
    });
  }

  /**
   * `GET users/details` is the only call that puts `roles` in the store on an LTI or SAML landing, so
   * the observable is handed back for the action to await instead of being subscribed here.
   */
  private usePersonalData(
    patchState: (val: Partial<UserAuthStateModel>) => UserAuthStateModel,
    getState: () => UserAuthStateModel,
    dispatch: (actions: UnauthenticateUser) => Observable<void>,
  ): Observable<unknown> {
    return this.accountService.getPersonalData().pipe(
      tap(({ isSuccess, value, error }) => {
        if (isSuccess) {
          patchState({
            ...getState(),
            userDetails: {
              ...getState().userDetails,
              error: undefined,
              data: {
                ...getState().userDetails.data,
                organization: getState().orgDetails?.data
                  ? { _id: getState().orgDetails.data._id, name: getState().orgDetails.data.name }
                  : value.organization,
                uid: value.uid,
                firstName: value.firstName,
                lastName: value.lastName,
                email: value.email,
                imageUrl: value.imageUrl ? value.imageUrl : 'assets/profile_image.png',
                description: value.description,
                roles: value.roles,
                groups: value.groups,
                eptid: value.eptid,
                workspaces: value.workspaces,
                userSettings: value.userSettings,
                orgClientAdminOf: value.orgClientAdminOf,
                dashboard: value.dashboard,
              },
            },
          });
          if (value.organization && getState().orgDetails?.data && !value.organization?._id.includes(getState().orgDetails.data._id)) {
            dispatch(new UserAuthActions.UnauthenticateUser());
          }
        } else {
          patchState({ userDetails: errorState(error) });
        }
      }),
    );
  }

  @Action(UserAuthActions.UpdateOrganizationPublisher)
  updateOrganizationPublisher(
    { patchState }: StateContext<UserAuthStateModel>,
    { publisher }: UserAuthActions.UpdateOrganizationPublisher,
  ) {
    patchState({ publisher: publisher });
  }

  @Action(UserAuthActions.LoadOrganizationPublisher)
  loadOrganizationPublisher({ patchState }: StateContext<UserAuthStateModel>) {
    return this.organizationRetrievalService.getCurrentOrganizationPublisher().pipe(
      tap(({ isSuccess, value }) => {
        if (isSuccess) {
          patchState({ publisher: value });
        } else {
          patchState({ publisher: undefined });
        }
      }),
    );
  }

  @Action(UserAuthActions.LoadOrganizationDetails)
  loadOrganizationDetails({ patchState }: StateContext<UserAuthStateModel>) {
    const domain = getOrganizationDomain();
    return this.organizationRetrievalService.findOrganizationForDomain(domain).pipe(
      tap(({ isSuccess, value, error }) => {
        if (isSuccess) {
          this.languageService.setOrganizationDefaultLanguage(value.defaultLanguage);
          this.languageService.setOrganizationLanguage(LanguageCodeHelper.getOrganizationDefaultLanguage(value));
          this.store.dispatch(new LanguagesActions.SetOrganizationLanguages(value.languagesSummary));
          ContentHelper.setOrgDiscoveryUrl(value.discoveryPage?.uri);
          patchState({
            orgDetails: dataLoadedState(value),
            featureFlagsState: this.buildFeatureFlagsStateFromOrganizationDetails(value),
          });
          if (value.pointClick) {
            this.store.dispatch(new UpdateOrganizationStyles(value.pointClick));
          }
        } else {
          patchState({ orgDetails: errorState(error) });
        }
      }),
    );
  }

  private buildFeatureFlagsStateFromOrganizationDetails(orgDetails: Organization): FeatureFlagsState {
    const featureFlagMapping: Record<FeatureFlag, keyof FeatureFlagsState> = {
      [FeatureFlag.AI_ASSISTANT]: 'aiAssistantFeatureFlagEnabled',
      [FeatureFlag.LMS_SKILLS]: 'lmsSkillsFeatureFlagEnabled',
      [FeatureFlag.EDITOR_JS]: 'editorJsFeatureFlagEnabled',
      [FeatureFlag.TIPTAP_EDITOR_V3]: 'tiptapEditorV3FeatureFlagEnabled',
      [FeatureFlag.PROFILE_CERTS]: 'profileCertificateFeatureFlagEnabled',
      [FeatureFlag.V3_CERTIFICATES]: 'v3CertificatesFeatureFlagEnabled',
      [FeatureFlag.V3_COMMUNITY]: 'v3CommunityFeatureFlagEnabled',
      [FeatureFlag.ENROLLMENT]: 'enrollmentFeatureFlagEnabled',
      [FeatureFlag.EMAIL_NOTIFICATIONS]: 'emailNotificationsFeatureFlagEnabled',
      [FeatureFlag.PAGE_ANALYTICS]: 'pageAnalyticsFeatureFlagEnabled',
      [FeatureFlag.CATEGORIES]: 'categoriesFeatureFlagEnabled',
      [FeatureFlag.EVENTS_TOP_BAR_HIDDEN]: 'eventsTopBarHiddenFeatureFlagEnabled',
      [FeatureFlag.TABLEAU_V2_ENABLED]: 'tableauV2FeatureFlagEnabled',
      [FeatureFlag.LMS_COURSE_BADGES]: 'lmsCourseBadgesFeatureFlagEnabled',
      [FeatureFlag.LMS_DEEP_LINKING_NEW_VIEW]: 'lmsDeepLinkingNewViewFeatureFlagEnabled',
      [FeatureFlag.HELP_CENTER]: 'helpCenterFeatureFlagEnabled',
      [FeatureFlag.EDITOR_JS_FORCE]: 'editorJsForceFeatureFlagEnabled',
      [FeatureFlag.SCORM]: 'scormFeatureFlagEnabled',
      [FeatureFlag.MENTORS]: 'mentorsFeatureFlagEnabled',
      [FeatureFlag.INDEPENDENT_BADGES]: 'badgesFeatureFlagEnabled',
      [FeatureFlag.FEEDBACK_360]: 'feedback360FeatureFlagEnabled',
      [FeatureFlag.ALLOW_RECORDING]: 'allowRecordingFeatureFlagEnabled',
      [FeatureFlag.PLAYLIST_COST]: 'playlistCostFeatureFlagEnabled',
      [FeatureFlag.FORCE_TWO_FACTOR_AUTHENTICATION]: 'forceTwoFactorAuthenticationFeatureFlagEnabled',
      [FeatureFlag.NEW_WORKBOOK_REVIEW_UI]: 'newWorkbookReviewUiFeatureFlagEnabled',
      [FeatureFlag.AI]: 'aiFeatureFlagEnabled',
      [FeatureFlag.AI_LER_EXP_GEN]: 'aiLerExpGenFeatureFlagEnabled',
      [FeatureFlag.AI_TEXT_ANALYSIS]: 'aiTextAnalysisFeatureFlagEnabled',
      [FeatureFlag.DOCUMENT_ANNOTATIONS]: 'documentAnnotationsFeatureFlagEnabled',
      [FeatureFlag.GOOGLE_CALENDAR_INTEGRATION]: 'googleCalendarIntegrationFeatureFlagEnabled',
      [FeatureFlag.OUTLOOK_INTEGRATION]: 'outlookIntegrationFeatureFlagEnabled',
      [FeatureFlag.CANVAS_COURSES_ANALYTICS]: 'canvasCoursesAnalyticsFeatureFlagEnabled',
      [FeatureFlag.NEW_PROJECT_DOWNLOAD]: 'newProjectDownloadFeatureFlagEnabled',
      [FeatureFlag.ANALYTICS_DASHBOARDS]: 'analyticsDashboardsFeatureFlagEnabled',
      [FeatureFlag.PLAYLIST_ANALYTICS_DASHBOARDS]: 'playlistAnalyticsDashboardsFeatureFlagEnabled',
      [FeatureFlag.COURSE_SETUP_DEV_PREVIEW]: 'courseSetupDevPreviewFeatureFlagEnabled',
      [FeatureFlag.MAGIC_LINK]: 'magicLinkFeatureFlagEnabled',
      [FeatureFlag.MESSAGE_BUTTON_ON_MENTEE_PROFILE]: 'messageButtonOnMenteeProfileFeatureFlagEnabled',
      [FeatureFlag.LMS_ASSIGNMENT_DEEPLINK]: 'lmsAssignmentDeeplinkFeatureFlagEnabled',
      [FeatureFlag.ASSIGNMENTS]: 'assignmentsFeatureFlagEnabled',
      [FeatureFlag.LTI_ASSIGNMENTS]: 'ltiAssignmentsFeatureFlagEnabled',
      [FeatureFlag.ASSIGNMENT_SHARING]: 'assignmentSharingFeatureFlagEnabled',
      [FeatureFlag.FORM_CAH_SUBJECT]: 'formCahSubjectFeatureFlagEnabled',
      [FeatureFlag.SHOW_PEERS_TO_LEARNER]: 'showPeersToLearnerFeatureFlagEnabled',
      [FeatureFlag.SHOW_SUBMISSIONS_AND_PROGRESS_TO_LEARNER]: 'showSubmissionsAndProgressToLearnerFeatureFlagEnabled',
    };

    return Object.fromEntries(
      Object.values(FeatureFlag).map((feature) => {
        const isEnabled = orgDetails.enabledFeatureFlags?.includes(feature) ?? false;
        return [featureFlagMapping[feature], isEnabled];
      }),
    ) as unknown as FeatureFlagsState;
  }

  @Action(UserAuthActions.AddDomainToOrganization)
  addDomainToOrganization(
    { getState, patchState }: StateContext<UserAuthStateModel>,
    { domain, direction }: UserAuthActions.AddDomainToOrganization,
  ) {
    const organization = getState().orgDetails.data;
    const observable =
      direction === 'white'
        ? this.organizationRetrievalService.addDomainToWhitelist(domain)
        : this.organizationRetrievalService.addDomainToBlacklist(domain);
    return observable.pipe(
      tap(({ isSuccess, error }) => {
        if (isSuccess) {
          const messageKey =
            direction === 'white'
              ? marker('translations.whitelist.message.success.addedToWhitelist')
              : marker('translations.whitelist.message.success.addedToBlacklist');
          SnackbarHelper.showTranslatableSnackBar(this.ngZone, this.snackBar, this.translocoService, messageKey);
          if (direction === 'white') {
            if (!organization.whitelist) {
              organization.whitelist = [];
            }
            organization.whitelist.push(domain);
          } else {
            if (!organization.blacklist) {
              organization.blacklist = [];
            }
            organization.blacklist.push(domain);
          }
          patchState({ orgDetails: dataLoadedState(organization) });
        } else {
          SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, error);
        }
      }),
    );
  }

  @Action(UserAuthActions.RemoveDomainFromOrganization)
  removeDomainFromOrganization(
    { getState, patchState }: StateContext<UserAuthStateModel>,
    { domain, direction }: UserAuthActions.RemoveDomainFromOrganization,
  ) {
    const organization = getState().orgDetails.data;
    const observable =
      direction === 'white'
        ? this.organizationRetrievalService.removeDomainFromWhitelist(domain)
        : this.organizationRetrievalService.removeDomainFromBlacklist(domain);
    return observable.pipe(
      tap(({ isSuccess, error }) => {
        if (isSuccess) {
          const messageKey =
            direction === 'white'
              ? marker('translations.whitelist.message.success.removeFromWhitelist')
              : marker('translations.whitelist.message.success.removeFromBlackList');
          SnackbarHelper.showTranslatableSnackBar(this.ngZone, this.snackBar, this.translocoService, messageKey);
          if (direction === 'white') {
            organization.whitelist = organization.whitelist.filter((item) => item !== domain);
          } else {
            organization.blacklist = organization.blacklist.filter((item) => item !== domain);
          }
          patchState({ orgDetails: dataLoadedState(organization) });
        } else {
          SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, error);
        }
      }),
    );
  }

  @Action(UserAuthActions.RemoveAllDomainsFromOrganization)
  removeAllDomainsFromOrganization(
    { getState, patchState }: StateContext<UserAuthStateModel>,
    { direction }: UserAuthActions.RemoveAllDomainsFromOrganization,
  ) {
    const organization = getState().orgDetails.data;
    const observable =
      direction === 'white'
        ? this.organizationRetrievalService.clearWhitelistDomains()
        : this.organizationRetrievalService.clearBlacklistDomains();
    return observable.pipe(
      tap(({ isSuccess, error }) => {
        if (isSuccess) {
          const messageKey =
            direction === 'white'
              ? marker('translations.whitelist.message.success.domainsRemovedFromWhitelist')
              : marker('translations.whitelist.message.success.domainsRemovedFromBlacklist');
          SnackbarHelper.showTranslatableSnackBar(this.ngZone, this.snackBar, this.translocoService, messageKey);
          if (direction === 'white') {
            organization.whitelist = [];
          } else {
            organization.blacklist = [];
          }
          patchState({ orgDetails: dataLoadedState(organization) });
        } else {
          SnackbarHelper.showSnackBar(this.ngZone, this.snackBar, error);
        }
      }),
    );
  }

  @Action(UserAuthActions.UpdateOrganizationStyles)
  updateOrganizationStyles(
    { getState, patchState }: StateContext<UserAuthStateModel>,
    { pointClicks }: UserAuthActions.UpdateOrganizationStyles,
  ) {
    const organization = getState().orgDetails.data;
    organization.pointClick = pointClicks;
    patchState({ orgDetails: dataLoadedState(organization) });
    this.updateRootStyles(pointClicks);
  }

  @Action(UserAuthActions.UnauthenticateUser)
  handlerUnauthentication(_: StateContext<UserAuthStateModel>, { redirectUri }: UserAuthActions.UnauthenticateUser) {
    return this.authService.unauthenticate(redirectUri);
  }

  @Action(UserAuthActions.FetchProfileImageDataUrl)
  fetchProfileImageDataUrl({ patchState, getState }: StateContext<UserAuthStateModel>) {
    const promise = new Promise<void>((resolve) => {
      const imageUrl = getState().userDetails?.data?.imageUrl;

      if (imageUrl?.startsWith('http')) {
        PrintPageHelper.fetchImageDataUrls([fetch(imageUrl)]).then((mapping) => {
          if (mapping[imageUrl]) {
            patchState({
              ...getState(),
              userDetails: {
                data: {
                  ...getState().userDetails.data,
                  imageUrl: mapping[imageUrl],
                },
                loading: false,
              },
            });
          }

          resolve();
        });
      } else {
        resolve();
      }
    });

    return from(promise);
  }

  private setPreviouslyLoggedInState(
    previouslyLoggedIn: boolean,
    patchState: (p: Partial<UserAuthStateModel>) => Partial<UserAuthStateModel>,
  ) {
    patchState({
      userPreviouslyLoggedIn: previouslyLoggedIn,
    });

    if (previouslyLoggedIn) {
      localStorage.setItem('userPreviouslyLoggedIn', 'true');
    } else {
      localStorage.removeItem('userPreviouslyLoggedIn');
    }
  }

  private checkUserSelectedLanguageOrientation(organization: Organization, languageCode: string) {
    this.directionService.applyOrganizationLanguage(organization, languageCode);
  }

  private updateRootStyles(pointClick: OrganizationTheme) {
    const v3Variables = Object.entries(deriveAccentFamily(pointClick.v3Styles?.brand))
      .map(([property, value]) => `${property}: ${value};`)
      .join('\n      ');

    this.styleElement.textContent = `:root {
      --theme-primary-color: ${pointClick.primaryThemeColor};
      --theme-primary-light-color: ${pointClick.primaryThemeLiteColor};
      --action-bar-color: ${pointClick.actionBarColor};
      --g-color11: ${pointClick.primaryColour};
      --g-color12: ${pointClick.primaryLiteColor};
      --g-color13: ${pointClick.interactiveColor};
      --g-color14: ${pointClick.interactiveLightColor};
      --g-color23: ${pointClick.adminColor};
      --g-color22: ${pointClick.adminLightColor};
      ${v3Variables}
     }`;
  }
}
